"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
exports.__esModule = true;
var Either_1 = require("fp-ts/lib/Either");
var function_1 = require("fp-ts/lib/function");
var Option_1 = require("fp-ts/lib/Option");
var pipeable_1 = require("fp-ts/lib/pipeable");
/**
 * @since 2.0.0
 */
exports.URI = '@nll/datum/datum';
/**
 * Constructs an initial `Datum` holding no value.
 *
 * @since 2.0.0
 */
exports.initial = {
    _tag: 'Initial'
};
/**
 * Constructs a pending `Datum` holding no value.
 *
 * @since 2.0.0
 */
exports.pending = {
    _tag: 'Pending'
};
/**
 * Constructs a new refresh `Datum` holding a value.
 *
 * @since 2.0.0
 */
exports.refresh = function (value) { return ({
    _tag: 'Refresh',
    value: value
}); };
/**
 * Constructs a new replete `Datum` holding a value.
 *
 * @since 2.0.0
 */
exports.replete = function (value) { return ({
    _tag: 'Replete',
    value: value
}); };
/**
 * @since 2.0.0
 */
exports.constInitial = function_1.constant(exports.initial);
/**
 * @since 2.0.0
 */
exports.constPending = function_1.constant(exports.pending);
/**
 * Takes a nullable value, if the value is not nully, turn it into a `Replete`, otherwise `Initial`.
 *
 * @since 2.0.0
 */
exports.fromNullable = function (a) {
    return a === null || a === undefined ? exports.initial : exports.replete(a);
};
/**
 * @since 2.0.0
 */
exports.fold = function (onInitial, onPending, onRefresh, onReplete) { return function (ma) {
    switch (ma._tag) {
        case 'Initial':
            return onInitial();
        case 'Pending':
            return onPending();
        case 'Refresh':
            return onRefresh(ma.value);
        case 'Replete':
            return onReplete(ma.value);
    }
}; };
/**
 * @since 2.0.0
 */
exports.getShow = function (S) { return ({
    show: exports.fold(function_1.constant('initial'), function_1.constant('pending'), function (v) { return "refresh(" + S.show(v) + ")"; }, function (v) { return "replete(" + S.show(v) + ")"; })
}); };
/**
 * @since 2.0.0
 */
exports.getEq = function (E) { return ({
    equals: function (x, y) {
        return x === y ||
            (exports.isReplete(x) && exports.isReplete(y) && E.equals(x.value, y.value)) ||
            (exports.isRefresh(x) && exports.isRefresh(y) && E.equals(x.value, y.value)) ||
            (exports.isInitial(x) && exports.isInitial(y)) ||
            (exports.isPending(x) && exports.isPending(y));
    }
}); };
/**
 * Semigroup returning the left-most non-`Initial` and non-`Pending` value. If both operands
 * are `Replete`s or `Refresh`s then the inner values are appended using the provided
 * `Semigroup` and refresh is coalesced if either are `Refresh`.
 *
 * @since 2.0.0
 */
exports.getSemigroup = function (S) { return ({
    concat: function (fx, fy) {
        return exports.fold(exports.constInitial, function_1.constant(exports.fold(exports.constInitial, exports.constPending, exports.constPending, exports.constPending)(fy)), function (x) {
            return exports.fold(exports.constInitial, exports.constPending, function (y) { return exports.refresh(S.concat(x, y)); }, function (y) { return exports.refresh(S.concat(x, y)); })(fy);
        }, function (x) {
            return exports.fold(exports.constInitial, exports.constPending, function (y) { return exports.refresh(S.concat(x, y)); }, function (y) { return exports.replete(S.concat(x, y)); })(fy);
        })(fx);
    }
}); };
var constZero = function_1.constant(0);
var constNegOne = function_1.constant(-1);
var constOne = function_1.constant(1);
/**
 * The `Ord` instance allows `Datum` values to be compared with
 * `compare`, whenever there is an `Ord` instance for
 * the type the `Datum` contains.
 *
 * `Initial` < `Pending` < `Refresh` | `Replete`
 *
 * @since 2.0.0
 */
function getOrd(O) {
    return {
        equals: exports.getEq(O).equals,
        compare: function (xa, ya) {
            return exports.fold(
            // x Initial
            function_1.constant(exports.fold(constZero, constNegOne, constNegOne, constNegOne)(ya)), 
            // x Pending
            function_1.constant(exports.fold(constOne, constZero, constNegOne, constNegOne)(ya)), 
            // x Refresh
            function (x) {
                return exports.fold(constOne, constOne, function (y) { return O.compare(x, y); }, constNegOne)(ya);
            }, 
            // x Replete
            function (x) {
                return exports.fold(constOne, constOne, constOne, function (y) { return O.compare(x, y); })(ya);
            })(xa);
        }
    };
}
exports.getOrd = getOrd;
/**
 * `Apply` semigroup
 *
 * @since 2.0.0
 */
exports.getApplySemigroup = function (S) {
    return exports.getSemigroup(S);
};
/**
 * @since 2.0.0
 */
exports.getApplyMonoid = function (M) { return (__assign(__assign({}, exports.getApplySemigroup(M)), { empty: exports.replete(M.empty) })); };
/**
 * Returns `true` if the Async is an instance of `Initial`, `false` otherwise
 *
 * @since 2.0.0
 */
exports.isInitial = function (ma) {
    return ma._tag === 'Initial';
};
/**
 * Returns `true` if the Async is an instance of `Pending`, `false` otherwise
 *
 * @since 2.0.0
 */
exports.isPending = function (ma) {
    return ma._tag === 'Pending';
};
/**
 * Returns `true` if the Async is an instance of `Refresh`, `false` otherwise
 *
 * @since 2.0.0
 */
exports.isRefresh = function (ma) {
    return ma._tag === 'Refresh';
};
/**
 * Returns `true` if the Async is an instance of `Replete`, `false` otherwise
 *
 * @since 2.0.0
 */
exports.isReplete = function (ma) {
    return ma._tag === 'Replete';
};
/**
 * @since 2.0.0
 */
exports.isValued = function (ma) {
    return exports.isReplete(ma) || exports.isRefresh(ma);
};
/**
 * @since 2.0.0
 */
exports.getOrElse = function (onInitial, onPending) { return function (ma) { return exports.fold(onInitial, onPending, function_1.identity, function_1.identity)(ma); }; };
/**
 * @since 2.0.0
 */
exports.elem = function (E) { return function (a, ma) {
    return exports.fold(function_1.constFalse, function_1.constFalse, function (b) { return E.equals(a, b); }, function (b) { return E.equals(a, b); })(ma);
}; };
/**
 * Returns `false` if `Refresh` or returns the result of the application of the given predicate to the `Replete` value.
 *
 * @since 2.0.0
 */
exports.exists = function (predicate) { return function (ma) {
    return exports.fold(function_1.constFalse, function_1.constFalse, predicate, predicate)(ma);
}; };
/**
 * @since 2.0.0
 */
var mapC = function (fa, f) {
    return exports.fold(exports.constInitial, exports.constPending, function (a) { return exports.refresh(f(a)); }, function (a) { return exports.replete(f(a)); })(fa);
};
/**
 * @since 2.0.0
 */
var apC = function (fab, fa) {
    return exports.fold(exports.constInitial, function () {
        return exports.fold(exports.constInitial, exports.constPending, exports.constPending, exports.constPending)(fa);
    }, function (f) {
        return exports.fold(exports.constInitial, exports.constPending, function (a) { return exports.refresh(f(a)); }, function (a) { return exports.refresh(f(a)); })(fa);
    }, function (f) {
        return exports.fold(exports.constInitial, exports.constPending, function (a) { return exports.refresh(f(a)); }, function (a) { return exports.replete(f(a)); })(fa);
    })(fab);
};
/**
 * @since 2.0.0
 */
var chainC = function (fa, f) {
    return exports.fold(exports.constInitial, exports.constPending, f, f)(fa);
};
/**
 * @since 2.0.0
 */
var reduceC = function (fa, b, f) {
    return exports.fold(function () { return b; }, function () { return b; }, function (a) { return f(b, a); }, function (a) { return f(b, a); })(fa);
};
/**
 * @since 2.0.0
 */
var foldMapC = function (M) { return function (fa, f) {
    return exports.fold(function () { return M.empty; }, function () { return M.empty; }, f, f)(fa);
}; };
/**
 * @since 2.0.0
 */
var reduceRightC = function (fa, b, f) {
    return exports.fold(function () { return b; }, function () { return b; }, function (a) { return f(a, b); }, function (a) { return f(a, b); })(fa);
};
/**
 * @since 2.0.0
 */
var traverseC = function (F) { return function (ta, f) {
    return exports.fold(function () { return F.of(exports.initial); }, function () { return F.of(exports.pending); }, function (a) { return F.map(f(a), exports.refresh); }, function (a) { return F.map(f(a), exports.replete); })(ta);
}; };
/**
 * @since 2.0.0
 */
var sequenceC = function (F) { return function (ta) {
    return exports.fold(function () { return F.of(exports.initial); }, function () { return F.of(exports.pending); }, function (a) { return F.map(a, exports.refresh); }, function (a) { return F.map(a, exports.replete); })(ta);
}; };
/**
 * @since 2.0.0
 */
var altC = function (fx, fy) {
    return exports.fold(fy, fy, exports.refresh, exports.replete)(fx);
};
/**
 * @since 2.0.0
 */
var extendC = function (wa, f) {
    return exports.replete(f(wa));
};
/**
 * @since 2.0.0
 */
var compactC = function (fa) {
    return exports.fold(exports.constInitial, exports.constPending, function (a) { return (Option_1.isSome(a) ? exports.refresh(a.value) : exports.initial); }, function (a) { return (Option_1.isSome(a) ? exports.replete(a.value) : exports.initial); })(fa);
};
var defaultSeparate = {
    left: exports.initial,
    right: exports.initial
};
/**
 * @since 2.0.0
 */
var separateC = function (fa) {
    var s = mapC(fa, function (e) { return ({
        left: Either_1.isLeft(e) ? exports.replete(e.left) : exports.initial,
        right: Either_1.isRight(e) ? exports.replete(e.right) : exports.initial
    }); });
    return exports.getOrElse(function () { return defaultSeparate; }, function () { return defaultSeparate; })(s);
};
/**
 * @since 2.0.0
 */
var filterC = function (fa, predicate) {
    return exports.fold(exports.constInitial, exports.constPending, function (a) { return (predicate(a) ? fa : exports.initial); }, function (a) { return (predicate(a) ? fa : exports.initial); })(fa);
};
/**
 * @since 2.0.0
 */
var filterMapC = function (ma, f) {
    return compactC(mapC(ma, f));
};
/**
 * @since 2.0.0
 */
var partitionC = function (fa, predicate) { return ({
    left: filterC(fa, function (a) { return !predicate(a); }),
    right: filterC(fa, predicate)
}); };
/**
 * @since 2.0.0
 */
var partitionMapC = function (fa, f) {
    return separateC(mapC(fa, f));
};
/**
 * @since 2.0.0
 */
var witherC = function (F) { return function (fa, f) {
    return exports.fold(function_1.constant(F.of(exports.initial)), function_1.constant(F.of(exports.pending)), function (a) { return F.map(f(a), function (o) { return (Option_1.isSome(o) ? exports.refresh(o.value) : exports.initial); }); }, function (a) { return F.map(f(a), function (o) { return (Option_1.isSome(o) ? exports.replete(o.value) : exports.initial); }); })(fa);
}; };
/**
 * @since 2.0.0
 */
var wiltC = function (F) { return function (fa, f) {
    var s = mapC(fa, function (a) {
        return F.map(f(a), function (e) { return ({
            left: Either_1.isLeft(e) ? exports.replete(e.left) : exports.initial,
            right: Either_1.isRight(e) ? exports.replete(e.right) : exports.initial
        }); });
    });
    return exports.isValued(s) ? s.value : F.of(defaultSeparate);
}; };
/**
 * @since 2.0.0
 */
var throwErrorC = function (e) { return exports.initial; };
/**
 * @since 2.0.0
 */
exports.datum = {
    URI: exports.URI,
    map: mapC,
    of: exports.replete,
    ap: apC,
    chain: chainC,
    reduce: reduceC,
    foldMap: foldMapC,
    reduceRight: reduceRightC,
    traverse: traverseC,
    sequence: sequenceC,
    zero: exports.constInitial,
    alt: altC,
    extend: extendC,
    compact: compactC,
    separate: separateC,
    filter: filterC,
    filterMap: filterMapC,
    partition: partitionC,
    partitionMap: partitionMapC,
    wither: witherC,
    wilt: wiltC,
    throwError: throwErrorC
};
var _a = pipeable_1.pipeable(exports.datum), alt = _a.alt, ap = _a.ap, apFirst = _a.apFirst, apSecond = _a.apSecond, chain = _a.chain, chainFirst = _a.chainFirst, duplicate = _a.duplicate, extend = _a.extend, filter = _a.filter, filterMap = _a.filterMap, flatten = _a.flatten, foldMap = _a.foldMap, map = _a.map, partition = _a.partition, partitionMap = _a.partitionMap, reduce = _a.reduce, reduceRight = _a.reduceRight, compact = _a.compact, separate = _a.separate, fromEither = _a.fromEither, filterOrElse = _a.filterOrElse, fromOption = _a.fromOption, fromPredicate = _a.fromPredicate;
exports.alt = alt;
exports.ap = ap;
exports.apFirst = apFirst;
exports.apSecond = apSecond;
exports.chain = chain;
exports.chainFirst = chainFirst;
exports.duplicate = duplicate;
exports.extend = extend;
exports.filter = filter;
exports.filterMap = filterMap;
exports.flatten = flatten;
exports.foldMap = foldMap;
exports.map = map;
exports.partition = partition;
exports.partitionMap = partitionMap;
exports.reduce = reduce;
exports.reduceRight = reduceRight;
exports.compact = compact;
exports.separate = separate;
exports.fromEither = fromEither;
exports.filterOrElse = filterOrElse;
exports.fromOption = fromOption;
exports.fromPredicate = fromPredicate;
//# sourceMappingURL=Datum.js.map